import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Radar,
    Loader2,
    AlertTriangle,
    ShieldAlert,
    Search,
    ExternalLink,
    CheckCircle2,
    Activity,
    Calendar,
    XCircle,
    Lock,
    ArrowRight,
} from "lucide-react";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { postJSON, getJSON } from "../lib/api";
import { useAuthStore } from "../state/authStore";

const SAMPLE_DOMAIN = "example.com";

const SEVERITY_PALETTE = {
    critical: { ring: "stroke-accent-red", text: "text-accent-red", bg: "bg-accent-red/10", border: "border-accent-red/40" },
    high:     { ring: "stroke-accent-amber", text: "text-accent-amber", bg: "bg-accent-amber/10", border: "border-accent-amber/40" },
    medium:   { ring: "stroke-yellow-400", text: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/30" },
    low:      { ring: "stroke-accent-mint", text: "text-accent-mint", bg: "bg-accent-mint/10", border: "border-accent-mint/40" },
};

// ============================ Shared result view ============================

export const AuditResultView = ({ scan, anon }) => {
    const findings = scan?.findings || {};
    const risk = scan?.risk_score || {};
    const palette = SEVERITY_PALETTE[risk.severity] || SEVERITY_PALETTE.medium;
    const subs = findings.subdomains || [];
    const score = typeof risk.score === "number" ? risk.score : 0;
    const dashArray = `${(score / 100) * 283} 283`;

    return (
        <div className="space-y-6" data-testid="audit-result-view">
            {/* Top: gauge + headline counters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Risk gauge */}
                <div className={`rounded-2xl border ${palette.border} ${palette.bg} p-5 flex flex-col items-center justify-center`} data-testid="audit-risk-gauge">
                    <svg width="120" height="120" viewBox="0 0 100 100" className="-rotate-90">
                        <circle cx="50" cy="50" r="45" stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
                        <circle
                            cx="50" cy="50" r="45" strokeWidth="8" fill="none"
                            className={palette.ring}
                            strokeDasharray={dashArray}
                            strokeLinecap="round"
                        />
                    </svg>
                    <div className={`-mt-[88px] text-center ${palette.text}`}>
                        <div className="text-3xl font-mono font-semibold leading-none">{score}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider mt-0.5">/100</div>
                    </div>
                    <div className="mt-12 text-center">
                        <span className={`inline-block text-[10.5px] font-mono uppercase tracking-wider px-2 py-1 rounded-md border ${palette.border} ${palette.text}`}>
                            {risk.severity || "unknown"} risk
                        </span>
                    </div>
                </div>

                {/* Subdomains counter */}
                <div className="rounded-2xl border border-white/[0.06] bg-elevated/40 p-5" data-testid="audit-counter-subdomains">
                    <div className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim mb-1">Subdomains discovered</div>
                    <div className="text-4xl font-mono text-accent-cyan">{findings.subdomains_count ?? subs.length}</div>
                    <div className="mt-2 text-[12px] text-text-muted">
                        via Certificate Transparency logs (crt.sh)
                    </div>
                    {findings.subdomains_truncated && (
                        <div className="mt-3 text-[11.5px] font-mono text-accent-amber inline-flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Showing top 20 — <Link to="/signup" className="underline hover:text-accent-cyan">sign up</Link> for the full list
                        </div>
                    )}
                </div>

                {/* Resolving counter */}
                <div className="rounded-2xl border border-white/[0.06] bg-elevated/40 p-5" data-testid="audit-counter-resolving">
                    <div className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim mb-1">Currently resolving</div>
                    <div className="text-4xl font-mono text-accent-mint">
                        {findings.subdomains_resolving ?? 0}
                        <span className="text-text-dim text-base"> / {findings.subdomains_count ?? subs.length}</span>
                    </div>
                    <div className="mt-2 text-[12px] text-text-muted">
                        live A/AAAA records — likely intentional exposure
                    </div>
                </div>
            </div>

            {/* Findings list */}
            {Array.isArray(risk.reasons) && risk.reasons.length > 0 && (
                <div className="rounded-2xl border border-white/[0.06] bg-surface p-5" data-testid="audit-findings">
                    <h2 className="font-display text-sm font-medium uppercase tracking-wider text-text-dim mb-3">
                        Findings
                    </h2>
                    <ul className="space-y-2">
                        {risk.reasons.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-[13px] text-text-primary leading-relaxed">
                                <ShieldAlert className="w-3.5 h-3.5 text-accent-amber shrink-0 mt-0.5" />
                                <span>{r}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Subdomain table */}
            <div className="rounded-2xl border border-white/[0.06] bg-surface overflow-hidden" data-testid="audit-subdomain-table">
                <header className="px-5 py-3 border-b border-white/[0.05] flex items-center justify-between">
                    <h2 className="font-display text-sm font-medium uppercase tracking-wider text-text-dim">
                        Subdomains
                    </h2>
                    <span className="text-[11.5px] font-mono text-text-dim">
                        Sorted by most recent certificate
                    </span>
                </header>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px]">
                        <thead>
                            <tr className="text-left text-[10.5px] font-mono uppercase tracking-wider text-text-dim border-b border-white/[0.04]">
                                <th className="px-5 py-2">Subdomain</th>
                                <th className="px-5 py-2">First seen</th>
                                <th className="px-5 py-2">IPs</th>
                                <th className="px-5 py-2">Issuer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subs.map((s, i) => (
                                <tr
                                    key={s.subdomain || i}
                                    className={`border-b border-white/[0.03] ${i % 2 === 0 ? "" : "bg-elevated/20"}`}
                                    data-testid={`audit-subdomain-row-${i}`}
                                >
                                    <td className="px-5 py-2 font-mono text-accent-cyan break-all">{s.subdomain}</td>
                                    <td className="px-5 py-2 font-mono text-text-dim whitespace-nowrap">
                                        {s.first_seen ? new Date(s.first_seen).toISOString().slice(0, 10) : "—"}
                                    </td>
                                    <td className="px-5 py-2 font-mono text-text-muted">
                                        {Array.isArray(s.ips) && s.ips.length > 0 ? (
                                            <span className="inline-flex items-center gap-1">
                                                <CheckCircle2 className="w-3 h-3 text-accent-mint" />
                                                {s.ips.slice(0, 2).join(", ")}{s.ips.length > 2 ? ` +${s.ips.length - 2}` : ""}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-text-dim">
                                                <XCircle className="w-3 h-3" />
                                                doesn't resolve
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-2 text-text-dim truncate max-w-[260px]">{s.issuer || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {findings.subdomains_truncated && (
                    <div className="px-5 py-3 border-t border-white/[0.05] bg-accent-cyan/[0.04] text-[12.5px] text-text-primary flex items-center gap-2" data-testid="audit-truncated-banner">
                        <Lock className="w-3.5 h-3.5 text-accent-cyan shrink-0" />
                        Anonymous view: showing top 20 of {findings.subdomains_total} subdomains.{" "}
                        <Link to="/signup" className="text-accent-cyan hover:underline">Sign up free</Link> for the full list.
                    </div>
                )}
            </div>

            {/* CTA */}
            <div className="rounded-2xl border border-accent-cyan/20 bg-accent-cyan/[0.04] p-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between" data-testid="audit-upgrade-cta">
                <div className="flex-1">
                    <h3 className="font-display text-base text-text-primary">
                        This MVP scan uses Certificate Transparency only.
                    </h3>
                    <p className="text-[13px] text-text-muted mt-1">
                        Upgrade to Pro for Shodan port + service detection, HIBP breach search,
                        and weekly delta alerts — or a one-time $4.99 scan (coming soon).
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link to="/pricing" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan text-canvas font-medium hover:shadow-glow-cyan transition-shadow" data-testid="audit-cta-pricing">
                        View Pro pricing <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

// ============================ Page: scan-and-show ============================

export default function PublicAuditTool() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const [domain, setDomain] = useState("");
    const [error, setError] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [scan, setScan] = useState(null);
    const [demo, setDemo] = useState(null);

    // Load the baked-in demo for the landing-page preview.
    useEffect(() => {
        getJSON("/audits/public/sample").then(setDemo).catch(() => null);
    }, []);

    const handleSubmit = async (e) => {
        e?.preventDefault();
        setError(null);
        setScan(null);
        const target = (domain || "").trim().toLowerCase();
        if (!target) {
            setError("Enter a domain to scan.");
            return;
        }
        setScanning(true);
        try {
            const created = await postJSON("/audits", { domain: target });
            // Poll for completion (max ~60s — crt.sh sometimes takes longer with retries).
            for (let i = 0; i < 40; i++) {
                await new Promise((r) => setTimeout(r, 1500));
                const fresh = await getJSON(`/audits/${created.scan_id}`);
                if (fresh.status === "complete" || fresh.status === "failed") {
                    setScan(fresh);
                    setScanning(false);
                    return;
                }
            }
            setError("Scan is taking longer than expected — open the result page directly.");
            navigate(`/tools/public-audit/${created.scan_id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            let msg;
            if (detail && typeof detail === "object") {
                msg = detail.message || detail.error || JSON.stringify(detail);
            } else if (typeof detail === "string") {
                msg = detail;
            } else {
                msg = e?.message || "Scan failed.";
            }
            setError(msg);
        } finally {
            setScanning(false);
        }
    };

    return (
        <main className="pt-24 md:pt-28 pb-20 mx-auto max-w-5xl px-5 md:px-10" data-testid="public-audit-page">
            <SEO
                title="Public Surface Audit — Discover Your Attack Surface"
                description="Free public-surface scan: discover subdomains, certificates, exposed services. Identify what attackers see about your domain — Certificate Transparency-powered, no setup required."
                canonicalUrl={`${SITE_DOMAIN}/tools/public-audit`}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "HowTo",
                    "name": "Run a free Public Surface Audit on any domain",
                    "step": [
                        { "@type": "HowToStep", "text": "Enter your domain (e.g., example.com) in the audit form." },
                        { "@type": "HowToStep", "text": "Submit — the engine queries Certificate Transparency logs for subdomains." },
                        { "@type": "HowToStep", "text": "Review the discovered subdomains, DNS resolution status, and risk score." },
                    ],
                }}
            />

            {/* Hero */}
            <header className="text-center mb-10" data-testid="public-audit-hero">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-accent-cyan/30 text-accent-cyan bg-accent-cyan/5">
                    <Radar className="w-3 h-3" />
                    New · Free 1 scan / day
                </span>
                <h1 className="mt-4 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-text-primary">
                    Public Surface Audit
                </h1>
                <p className="mt-3 text-text-muted text-base max-w-2xl mx-auto leading-relaxed">
                    Discover what attackers see about your company. Subdomains, certificates,
                    exposed services — all from public sources. No agent. No setup.
                </p>
            </header>

            {/* Form */}
            <form onSubmit={handleSubmit} className="mb-10" data-testid="public-audit-form">
                <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-dim" />
                        <input
                            type="text"
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                            placeholder="example.com"
                            disabled={scanning}
                            className="w-full pl-10 pr-3 py-3 rounded-md bg-surface border border-white/10 text-text-primary placeholder:text-text-dim font-mono focus:outline-none focus:ring-2 focus:ring-accent-cyan/30 disabled:opacity-50"
                            data-testid="public-audit-domain-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={scanning}
                        className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-md bg-accent-cyan text-canvas font-medium hover:shadow-glow-cyan transition-shadow disabled:opacity-60"
                        data-testid="public-audit-submit"
                    >
                        {scanning ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Scanning Certificate Transparency logs…
                            </>
                        ) : (
                            <>
                                <Radar className="w-4 h-4" />
                                Run audit
                            </>
                        )}
                    </button>
                </div>
                {!user && (
                    <p className="mt-2 text-[11.5px] text-text-dim">
                        Anonymous: 1 scan / 24 h, top 20 subdomains.{" "}
                        <Link to="/signup" className="text-accent-cyan hover:underline">Sign up free</Link> for the full list.
                    </p>
                )}
                {error && (
                    <div className="mt-3 flex items-start gap-2 text-[12.5px] text-accent-red bg-accent-red/[0.05] border border-accent-red/30 rounded-md p-3" data-testid="public-audit-error">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}
            </form>

            {/* Result view */}
            {scan && (
                <section className="mb-12" data-testid="public-audit-result">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="font-display text-xl text-text-primary">
                                Audit of <span className="font-mono text-accent-cyan">{scan.domain}</span>
                            </h2>
                            <p className="text-[12px] font-mono text-text-dim mt-0.5 inline-flex items-center gap-2">
                                <Activity className="w-3 h-3" /> scan_id {scan.scan_id}
                                {scan.created_at && (
                                    <>
                                        <span>·</span>
                                        <Calendar className="w-3 h-3" />
                                        {new Date(scan.created_at).toLocaleString()}
                                    </>
                                )}
                            </p>
                        </div>
                        <Link
                            to={`/tools/public-audit/${scan.scan_id}`}
                            className="text-[12.5px] text-accent-cyan hover:underline inline-flex items-center gap-1"
                            data-testid="public-audit-permalink"
                        >
                            Permalink <ExternalLink className="w-3 h-3" />
                        </Link>
                    </div>
                    <AuditResultView scan={scan} anon={!user} />
                </section>
            )}

            {/* Demo preview */}
            {!scan && demo && (
                <section className="opacity-95" data-testid="public-audit-demo-section">
                    <header className="mb-4">
                        <span className="text-[10.5px] font-mono uppercase tracking-wider text-text-dim">
                            Example output · <span className="font-mono text-text-primary">{demo.domain}</span>
                        </span>
                        <h2 className="font-display text-xl text-text-primary mt-1">
                            What you'll see after a scan
                        </h2>
                    </header>
                    <AuditResultView scan={demo} anon={!user} />
                </section>
            )}
        </main>
    );
}
